import {
  HTMLProps,
  isValidElement,
  MutableRefObject,
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  U21HelpModal,
  U21HelpModalProps,
} from 'app/shared/u21-ui/components/display/U21HelpModal';
import { U21Typography } from 'app/shared/u21-ui/components/display/typography/U21Typography';
import { U21Loading } from 'app/shared/u21-ui/components/display/U21Loading';
import { U21Spacer } from 'app/shared/u21-ui/components/layout/U21Spacer';

import { getDOMProps } from 'app/shared/utils/react';
import styled, { css } from 'styled-components';
import { useTheme } from '@mui/material/styles';

type PageSizeType = 'medium' | 'max';

export interface U21PageProps
  extends Omit<HTMLProps<HTMLDivElement>, 'action' | 'size' | 'title'> {
  action?: ReactNode;
  afterTitleContent?: ReactNode;
  beforeTitleContent?: ReactNode;
  belowTitleContent?: ReactNode;
  children: ReactNode;
  footerContent?: ReactNode;
  helpModalProps?: U21HelpModalProps;
  loading?: boolean;
  size?: PageSizeType;
  title: ReactNode;
  titleIcon?: ReactElement;
}

export const U21PageNew = (props: U21PageProps) => {
  const {
    action,
    afterTitleContent,
    beforeTitleContent,
    belowTitleContent,
    children,
    footerContent,
    helpModalProps,
    loading,
    size = 'max',
    title,
    titleIcon,
    ...rest
  } = props;

  const headerContainerRef: MutableRefObject<HTMLDivElement | null> =
    useRef(null);
  const contentRef: MutableRefObject<HTMLDivElement | null> = useRef(null);
  const [headerElement, setHeaderElement] = useState<HTMLDivElement | null>(
    null,
  );

  const theme = useTheme();

  const hasBelowTitleContent = Boolean(belowTitleContent);

  const [headerHeight, setHeaderHeight] = useState(0);
  const [isScrollReady, setIsScrollReady] = useState(false);

  const headerContainerRefCallback = useCallback(
    (node: HTMLDivElement | null) => {
      headerContainerRef.current = node;
      setIsScrollReady(
        Boolean(headerContainerRef.current && contentRef.current),
      );
    },
    [],
  );

  const contentRefCallback = useCallback((node: HTMLDivElement | null) => {
    contentRef.current = node;
    setIsScrollReady(Boolean(headerContainerRef.current && contentRef.current));
  }, []);

  useEffect(() => {
    if (!hasBelowTitleContent) {
      return undefined;
    }
    if (!headerElement) {
      return undefined;
    }

    const resizeObserver = new ResizeObserver(() => {
      setHeaderHeight(headerElement.offsetHeight - 16);
    });
    resizeObserver.observe(headerElement);
    return () => {
      resizeObserver.disconnect();
    };
  }, [hasBelowTitleContent, headerElement]);

  useEffect(() => {
    const contentElement = contentRef.current;
    const headerContainerElement = headerContainerRef.current;
    if (!isScrollReady || !contentElement || !headerContainerElement) {
      return undefined;
    }

    const onScroll = () => {
      const { scrollTop } = contentElement;

      // Handle shadow effect
      headerContainerElement.style.boxShadow = scrollTop
        ? theme.customShadows.z12
        : 'none';

      if (!isScrollReady || !hasBelowTitleContent) {
        return;
      }

      // Handle header transformation slide up to give content more space
      if (scrollTop < headerHeight) {
        headerContainerElement.style.transform = `translateY(-${scrollTop}px)`;
        contentElement.style.marginTop = `-${scrollTop}px`;
      } else {
        headerContainerElement.style.transform = `translateY(-${headerHeight}px)`;
        contentElement.style.marginTop = `-${headerHeight}px`;
      }
    };

    contentElement.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      contentElement.removeEventListener('scroll', onScroll);
    };
  }, [
    hasBelowTitleContent,
    headerHeight,
    isScrollReady,
    theme.customShadows.z12,
  ]);

  return (
    <Container {...getDOMProps(rest)}>
      {!loading && (
        <>
          <HeaderContainer $size={size} ref={headerContainerRefCallback}>
            <Header horizontal ref={setHeaderElement} useFlexGap>
              <HeaderSpacer horizontal>
                {beforeTitleContent}
                {isValidElement(title) ? (
                  title
                ) : (
                  <U21Typography
                    align="start"
                    ellipsis
                    icon={titleIcon}
                    variant="h4"
                  >
                    {title}
                  </U21Typography>
                )}
                {afterTitleContent}
                {helpModalProps && (
                  <U21HelpModal
                    title="How to Use This Page"
                    {...helpModalProps}
                  />
                )}
              </HeaderSpacer>
              {action}
            </Header>
            {belowTitleContent}
          </HeaderContainer>
          <Content
            $hasFooterContent={Boolean(footerContent)}
            ref={contentRefCallback}
            $size={size}
          >
            {children}
          </Content>
          {Boolean(footerContent) && (
            <FooterContainer>{footerContent}</FooterContainer>
          )}
        </>
      )}
      <MaxLoading loading={loading} />
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  background: ${(props) => props.theme.palette.background.neutral};
`;

const SIZE_PADDING_OVERRIDE_STYLE = css<{ $size: PageSizeType }>`
  ${(props) => {
    switch (props.$size) {
      case 'medium':
        // set a max width of 1280px by adding padding
        return css`
          padding-left: max(calc((100% - 1280px) / 2), 24px);
          padding-right: max(calc((100% - 1280px) / 2), 24px);
        `;
      default:
        return css``;
    }
  }}
`;

const HeaderContainer = styled.div<{ $size: PageSizeType }>`
  z-index: 1;
  transition: transform 0.05s linear;
  padding: 0 24px;
  ${SIZE_PADDING_OVERRIDE_STYLE}
`;

const Header = styled(U21Spacer)`
  justify-content: space-between;
  padding: 24px 0 16px;
  background: ${(props) => props.theme.palette.background.neutral};
  transition: ${(props) => props.theme.transitions.create(['all'])};
`;

const Content = styled.div<{
  $hasFooterContent?: boolean;
  $size: PageSizeType;
}>`
  flex: 1;
  overflow: auto;
  padding: 0 24px ${(props) => (props.$hasFooterContent ? 0 : 24)}px;
  background: ${(props) => props.theme.palette.background.neutral};
  transition: margin-top 0.05s linear;
  ${SIZE_PADDING_OVERRIDE_STYLE}
`;

const HeaderSpacer = styled(U21Spacer)`
  overflow: hidden;
`;

const MaxLoading = styled(U21Loading)`
  // factor in margin for height + width
  height: calc(100% - 48px);
  width: calc(100% - 48px);
  margin: 24px;
`;

const FooterContainer = styled.div`
  padding: 8px 24px;
`;
